// Thin same-origin API client. The server is the only authority; this module just carries the
// session cookie (browser-managed, HttpOnly), the CSRF token and idempotency keys.
import { DEMO_SANDBOX_HEADER, DEMO_SANDBOX_IDLE_HEADER, type ApiErrorBody, type ErrorCode } from '@passage/contracts';
import { isLocale, translate } from '@passage/i18n';

let csrfToken: string | null = null;
let requestLocale = 'en';
/** PD-015: the API localises messages from Accept-Language. */
export function setRequestLocale(locale: string): void {
  requestLocale = locale;
}
export function setCsrfToken(token: string | null): void {
  csrfToken = token;
}

/**
 * PB-12-07 / PD-066: commands to site-bound operational areas carry the operator's current working
 * site; the server refuses one aimed at a resource of another site (a page left open from before a
 * switch). Areas where staff legitimately manage several sites from one page are not listed.
 */
export const SITE_BOUND_PATHS: readonly RegExp[] = [/^\/api\/v1\/reception\//, /^\/api\/v1\/visits(\/|$|\?)/, /^\/api\/v1\/gate\//, /^\/api\/v1\/vehicles\//, /^\/api\/v1\/visit-vehicles\//, /^\/api\/v1\/parking\//, /^\/api\/v1\/cards\//, /^\/api\/v1\/credentials\//];
let workingSite: string | null = null;
export function setWorkingSite(id: string | null): void {
  workingSite = id;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ErrorCode | 'NETWORK';
  readonly fields: Readonly<Record<string, string>>;
  readonly details: Readonly<Record<string, unknown>>;
  readonly correlationId: string | null;
  constructor(status: number, body: ApiErrorBody | null, fallback: string) {
    super(body?.error.message ?? fallback);
    this.status = status;
    this.code = body?.error.code ?? (status === 0 ? 'NETWORK' : 'INTERNAL');
    this.fields = body?.error.fields ?? {};
    this.details = body?.error.details ?? {};
    this.correlationId = body?.error.correlationId ?? null;
  }
}

function isErrorBody(v: unknown): v is ApiErrorBody {
  if (typeof v !== 'object' || v === null) return false;
  const e = (v as { error?: unknown }).error;
  return typeof e === 'object' && e !== null && typeof (e as { code?: unknown }).code === 'string' && typeof (e as { message?: unknown }).message === 'string';
}

/**
 * PB-13-01: a failed response becomes an ApiRequestError. The server's message is already safe and
 * localised; any other body (a proxy error page, empty or malformed JSON) gets the catalogue
 * fallback in the request language, never raw response text (PD-435).
 */
export function responseError(status: number, text: string, locale: string = requestLocale): ApiRequestError {
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = null;
  }
  const err = new ApiRequestError(status, isErrorBody(parsed) ? parsed : null, translate(isLocale(locale) ? locale : 'en', 'error.requestFailedStatus', { status }));
  if (err.code === 'DEMO_READ_ONLY') announceDemoReadOnly();
  return err;
}

/** PD-730: window event raised whenever the API refuses a change because this is a demo. */
export const DEMO_READ_ONLY_EVENT = 'passage:demo-read-only';

function announceDemoReadOnly(): void {
  // globalThis, not window: this module is also type-checked by the browser-test project (no DOM lib).
  const target = globalThis as { dispatchEvent?: (e: Event) => boolean };
  if (typeof target.dispatchEvent === 'function') target.dispatchEvent(new Event(DEMO_READ_ONLY_EVENT));
}

/** PD-776 (demosandbox stream): window event raised when a demo answer worked in this browser's sandbox. */
export const DEMO_SANDBOX_EVENT = 'passage:demo-sandbox';

/** 'active' = kept for this browser only until `idleSeconds` without use; 'fallback' = shown once, not kept. */
export interface DemoSandboxNote {
  readonly state: 'active' | 'fallback';
  readonly idleSeconds: number;
}

/** PD-776: reads how a demo answer ran (response headers) and tells the demo notice. */
export function noteDemoSandbox(res: { readonly ok: boolean; readonly headers: { get(name: string): string | null } }): DemoSandboxNote | null {
  if (!res.ok) return null;
  const state = res.headers.get(DEMO_SANDBOX_HEADER);
  if (state !== 'active' && state !== 'fallback') return null;
  const idle = Number(res.headers.get(DEMO_SANDBOX_IDLE_HEADER));
  const note: DemoSandboxNote = { state, idleSeconds: Number.isFinite(idle) && idle > 0 ? idle : 600 };
  // globalThis, not window: this module is also type-checked by the browser-test project (no DOM lib).
  const target = globalThis as { dispatchEvent?: (e: Event) => boolean };
  if (typeof target.dispatchEvent === 'function') target.dispatchEvent(Object.assign(new Event(DEMO_SANDBOX_EVENT), { detail: note }));
  return note;
}

/** PD-730: a demo refusal is information, not a failure (no error page, no retry). */
export function isDemoReadOnly(e: unknown): boolean {
  return e instanceof ApiRequestError && e.code === 'DEMO_READ_ONLY';
}

/** A fresh key per user intent; reuse the same key when retrying that same intent. */
export function newIdempotencyKey(): string {
  const bytes = new Uint8Array(18);
  crypto.getRandomValues(bytes);
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export async function api<T>(method: Method, path: string, body?: unknown, opts: { idempotencyKey?: string } = {}): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', 'accept-language': requestLocale };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (method !== 'GET' && csrfToken) headers['x-csrf-token'] = csrfToken;
  if (opts.idempotencyKey) headers['idempotency-key'] = opts.idempotencyKey;
  if (method !== 'GET' && workingSite && SITE_BOUND_PATHS.some((r) => r.test(path))) headers['x-passage-site'] = workingSite;
  let res: Response;
  try {
    res = await fetch(path, { method, headers, credentials: 'same-origin', ...(body !== undefined ? { body: JSON.stringify(body) } : {}) });
  } catch {
    // The outcome of a mutation is unknown here; callers retry with the same idempotency key.
    // The message key is translated by ErrorState (code NETWORK).
    throw new ApiRequestError(0, null, 'error.network');
  }
  noteDemoSandbox(res);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (!res.ok) throw responseError(res.status, text);
  return (text ? JSON.parse(text) : null) as T;
}

export const get = <T>(path: string) => api<T>('GET', path);

/** Binary upload with optimistic version (If-Match) — used for logos and pictures. */
export async function uploadFile<T>(method: 'PUT', path: string, file: File, version: number): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', 'accept-language': requestLocale, 'content-type': file.type || 'application/octet-stream', 'if-match': String(version) };
  if (csrfToken) headers['x-csrf-token'] = csrfToken;
  let res: Response;
  try {
    res = await fetch(path, { method, headers, credentials: 'same-origin', body: file });
    noteDemoSandbox(res); // PD-776 / PD-810
  } catch {
    throw new ApiRequestError(0, null, 'error.network');
  }
  const text = await res.text();
  if (!res.ok) throw responseError(res.status, text);
  return (text ? JSON.parse(text) : null) as T;
}

/** Visitor document upload on a pass link: authorised by the invitation token header, no session or CSRF. */
export async function uploadWithToken<T>(path: string, file: File, token: string): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', 'accept-language': requestLocale, 'content-type': file.type || 'application/octet-stream', 'x-invitation-token': token };
  let res: Response;
  try {
    res = await fetch(path, { method: 'PUT', headers, credentials: 'omit', body: file });
  } catch {
    throw new ApiRequestError(0, null, 'error.network');
  }
  const text = await res.text();
  if (!res.ok) throw responseError(res.status, text);
  return (text ? JSON.parse(text) : null) as T;
}

/** DELETE with If-Match. */
export async function deleteWithVersion<T>(path: string, version: number): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json', 'accept-language': requestLocale, 'if-match': String(version) };
  if (csrfToken) headers['x-csrf-token'] = csrfToken;
  const res = await fetch(path, { method: 'DELETE', headers, credentials: 'same-origin' });
  noteDemoSandbox(res); // PD-776 / PD-810
  const text = await res.text();
  if (!res.ok) throw responseError(res.status, text);
  return (text ? JSON.parse(text) : null) as T;
}
